import { useEffect } from 'react'
import Router from 'next/router'
import { addPageAction } from '@lib/newrelic/agent'

const RELOAD_STORAGE_KEY = 'last-network-error-reload'
const RELOAD_THROTTLE_MS = 10000

const shouldReloadFromMessage = (message: string) => {
    const msg = message.toLowerCase()

    return (
        msg.includes('loading chunk') ||
        msg.includes('chunkloaderror') ||
        msg.includes('failed to load chunk')
    )
}

const safeReload = () => {
    const lastReload = sessionStorage.getItem(RELOAD_STORAGE_KEY)
    const now = Date.now()

    if (!lastReload || now - parseInt(lastReload, 10) > RELOAD_THROTTLE_MS) {
        sessionStorage.setItem(RELOAD_STORAGE_KEY, now.toString())
        // Marca el reload por ChunkLoadError en New Relic (no es un error nuevo:
        // el agente ya auto-captura el error global; esto solo aporta contexto).
        addPageAction('chunkErrorReload', { url: window.location.href })
        window.location.reload()
    }
}

/**
 * Recuperación ante ChunkLoadError. Usa el singleton Router.events (deps [])
 * para evitar re-suscripciones en loop con Next 16.
 */
export const useAppErrorRecovery = () => {
    useEffect(() => {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason
            const message =
                typeof reason === 'string' ? reason : reason?.message || ''

            if (
                typeof message === 'string' &&
                shouldReloadFromMessage(message)
            ) {
                safeReload()
            }
        }

        const handleWindowError = (event: ErrorEvent) => {
            const message = event.message || ''

            if (shouldReloadFromMessage(message)) {
                safeReload()
            }
        }

        const handleRouteError = (err: unknown) => {
            const message =
                typeof err === 'object' && err && 'message' in err
                    ? String((err as { message?: string }).message || '')
                    : ''

            if (shouldReloadFromMessage(message)) {
                safeReload()
            }
        }

        window.addEventListener('unhandledrejection', handleUnhandledRejection)
        window.addEventListener('error', handleWindowError)
        Router.events.on('routeChangeError', handleRouteError)

        return () => {
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection
            )
            window.removeEventListener('error', handleWindowError)
            Router.events.off('routeChangeError', handleRouteError)
        }
    }, [])
}
